/**
 * Tenant-slug context.
 *
 * SerSo-intern users select a tenant (or "all") via the sidebar tenant
 * switcher. The slug is forwarded to the backend as `X-Tenant-Slug` by every
 * `apiRequest` call that opts in (each feature passes `tenant: useTenant().slug`).
 *
 *  - "all" means "cross-tenant"; backend RLS interprets this per role and may
 *    audit-log the call (see PROJECT_BRIEF.md §12).
 *  - State is persisted under `localStorage["serso.tenant"]` so a reload
 *    remembers the last selection.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type TenantSlug = string | 'all';

export type TenantContextValue = {
  slug: TenantSlug;
  setSlug: (s: TenantSlug) => void;
};

const STORAGE_KEY = 'serso.tenant';

const noop: TenantContextValue = {
  slug: 'all',
  setSlug: () => {
    // Default no-op so consumers outside the provider don't crash in dev.
  },
};

export const TenantContext = createContext<TenantContextValue>(noop);

function readPersisted(initial: TenantSlug): TenantSlug {
  if (typeof window === 'undefined') return initial;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored as TenantSlug;
  } catch {
    // localStorage may throw (Safari private mode, etc.) — fall through.
  }
  return initial;
}

export type TenantProviderProps = {
  children: ReactNode;
  initialSlug?: TenantSlug;
};

export function TenantProvider({
  children,
  initialSlug = 'all',
}: TenantProviderProps): JSX.Element {
  const [slug, setSlugState] = useState<TenantSlug>(() => readPersisted(initialSlug));

  // Persist on every change. Wrapped in try/catch because storage quota /
  // disabled-storage browsers throw synchronously.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, slug);
    } catch {
      // Best-effort persistence.
    }
  }, [slug]);

  const setSlug = useCallback((s: TenantSlug) => {
    setSlugState(s);
  }, []);

  const value = useMemo<TenantContextValue>(() => ({ slug, setSlug }), [slug, setSlug]);

  return createElement(TenantContext.Provider, { value }, children);
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}
